/**
 * Anthra AI — the host adapter.
 *
 * WHY THIS EXISTS
 * ===============
 * `@anthra/platform` is shared by web apps and by Expo apps. Almost all of it —
 * the log record shape, redaction, the error taxonomy, the event contract, Zod
 * parsing, retry policy — is pure TypeScript and runs anywhere. A handful of
 * things are not, and those are what this interface isolates.
 *
 * The temptation is to guard each one with `typeof window === 'undefined'`.
 * THAT DOES NOT WORK ON REACT NATIVE, and the reason is worth knowing because
 * it produces a crash that looks like a bundler problem:
 *
 *   React Native DEFINES a global `window` (it aliases `global`). It does NOT
 *   define `document`. So this guard, which reads as "skip on non-browser":
 *
 *       if (typeof window === 'undefined') return;
 *       document.addEventListener('visibilitychange', ...);   // throws on RN
 *
 *   ...passes on native and then throws on the next line, at module import
 *   time, before any of your code runs.
 *
 * Three concrete incompatibilities, all of which were present in this package
 * before the adapter existed:
 *
 *   1. `import.meta.env`  — Vite syntax. Metro cannot parse `import.meta` under
 *      the default Expo Babel preset, so this is a BUILD failure, not a runtime
 *      one. Native uses `process.env.EXPO_PUBLIC_*`.
 *
 *   2. `document` / `window` listeners — see above. Native has `AppState`.
 *
 *   3. `localStorage` — absent on native. The old code fell back to generating a
 *      fresh anonymous ID, which meant EVERY APP LAUNCH LOOKED LIKE A NEW USER.
 *      Nothing errors; retention, funnels and DAU are simply wrong, in the
 *      direction that flatters the numbers.
 *
 * THE SHAPE OF THE INTERFACE
 * ==========================
 * Storage is ASYNC even though `localStorage` is synchronous. Async is the
 * lowest common denominator — SecureStore and AsyncStorage are both async, and
 * there is no way to make them synchronous. Pretending otherwise would put the
 * incompatibility back one layer down.
 *
 * INSTALLATION IS EXPLICIT
 * ========================
 * No `.native.ts` file extensions, no `exports` conditions, no bundler magic.
 * The app's entry point calls `installHost()` before anything else. It is one
 * line, it is greppable, and when it is missing you get a clear error instead of
 * a subtly different code path silently selected by a resolver you did not know
 * was involved.
 */

export type KeyValueStore = {
	get(key: string): Promise<string | null>
	set(key: string, value: string): Promise<void>
	remove(key: string): Promise<void>
}

export type AppInfo = {
	/** 'web' | 'ios' | 'android' */
	platform: string
	/** Marketing version — 1.4.0 */
	version: string
	/**
	 * Native build number, or the web build hash. Distinct from `version`: two
	 * builds of the same version DO differ, and when a crash affects only one of
	 * them this is the field that says so.
	 */
	buildId: string
	locale: string
	/**
	 * Expo update ID when running an OTA update, otherwise undefined.
	 *
	 * On native this is the single most useful field in a crash report. The
	 * binary in the store and the JavaScript actually running are different
	 * artefacts, and without this you cannot tell which JS bundle a user was on.
	 */
	updateId?: string
}

export type Host = {
	readonly name: "web" | "native"

	/**
	 * Build-time configuration. Web reads `import.meta.env`, native reads
	 * `process.env.EXPO_PUBLIC_*`. Both are PUBLIC — inlined into the bundle and
	 * readable by anyone who downloads the app. Never a secret, on either.
	 */
	env(key: string): string | undefined

	/** Ordinary persistence. AsyncStorage on native, localStorage on web. */
	storage: KeyValueStore

	/**
	 * Credential persistence.
	 *
	 * Native: Keychain (iOS) / Keystore (Android) via expo-secure-store —
	 * OS-level encryption, and not readable by another app.
	 *
	 * Web: IN MEMORY ONLY. There is no secure store in a browser; localStorage is
	 * readable by any XSS payload, so the refresh token stays in an HttpOnly
	 * cookie the JavaScript never sees. This is the one place the two platforms
	 * genuinely differ in what they can promise, so the difference is in the type
	 * system's face rather than buried in a comment: on web, `secureStorage` does
	 * not survive a reload, and that is correct.
	 */
	secureStorage: KeyValueStore

	/**
	 * Fires when the app is backgrounded, so buffers can be flushed.
	 * Web: visibilitychange / pagehide. Native: AppState 'background'.
	 * Returns an unsubscribe function.
	 */
	onBackground(callback: () => void): () => void

	/**
	 * Best-effort delivery that survives teardown. Returns false when the host
	 * has no such mechanism, and the caller falls back to fetch.
	 *
	 * Only web has one (`navigator.sendBeacon`). On native a backgrounded app may
	 * simply be suspended mid-request, which is why the native logger flushes on
	 * the background event rather than trusting the request to complete.
	 */
	beacon(url: string, body: string): boolean

	/**
	 * Catches the two error classes that are otherwise completely invisible: an
	 * uncaught error outside React's tree, and an unhandled promise rejection.
	 * Web uses window events; native uses `ErrorUtils.setGlobalHandler`.
	 */
	installGlobalErrorHandler(
		handler: (error: unknown, context: Record<string, unknown>) => void,
	): void

	appInfo(): AppInfo
}

// -----------------------------------------------------------------------------
// The installed host
// -----------------------------------------------------------------------------

let installed: Host | undefined

/**
 * Install the host. Call this FIRST, before importing anything that logs.
 *
 *   // web: src/main.tsx
 *   import { installHost } from '@anthra/platform/host'
 *   import { webHost } from '@anthra/platform/host/web'
 *   installHost(webHost())
 *
 *   // native: app/_layout.tsx
 *   import { installHost } from '@anthra/platform/host'
 *   import { nativeHost } from '@anthra/platform-native'
 *   installHost(nativeHost())
 */
export function installHost(adapter: Host): void {
	// Named `adapter`, not `host`: a parameter called `host` shadows the
	// `host()` accessor declared below, so the identifier means two different
	// things a few lines apart.
	installed = adapter
}

/**
 * The installed host, or a throwing stub.
 *
 * The stub is deliberate. The alternative — defaulting to the web host — means
 * an Expo app with a missing `installHost` call runs, appears to work, and fails
 * only in whichever code path first touches `document`. A named error at the
 * first use is a five-minute fix; the other is an afternoon.
 */
export function host(): Host {
	if (!installed) {
		throw new Error(
			"@anthra/platform: no host installed. Call installHost(webHost()) from your " +
				"web entry point, or installHost(nativeHost()) from app/_layout.tsx, before " +
				"anything that logs or calls the API.",
		)
	}
	return installed
}

/** True once a host is installed. For code that must degrade rather than throw. */
export function hasHost(): boolean {
	return installed !== undefined
}

/**
 * A host that does nothing, for unit tests.
 *
 * Storage is a Map, so a test that writes and reads a token behaves like the
 * real thing without touching a browser API or a native module.
 */
export function memoryHost(overrides: Partial<Host> = {}): Host {
	const make = (): KeyValueStore => {
		const data = new Map<string, string>()
		return {
			get: key => Promise.resolve(data.get(key) ?? null),
			set: (key, value) => {
				data.set(key, value)
				return Promise.resolve()
			},
			remove: key => {
				data.delete(key)
				return Promise.resolve()
			},
		}
	}

	return {
		name: "web",
		env: () => undefined,
		storage: make(),
		secureStorage: make(),
		onBackground: () => () => undefined,
		beacon: () => false,
		installGlobalErrorHandler: () => undefined,
		appInfo: () => ({
			platform: "test",
			version: "0.0.0",
			buildId: "test",
			locale: "en-IN",
		}),
		...overrides,
	}
}
